import {
  ActionIcon,
  Box,
  createStyles,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {EventTriggerConfig, Routine, TimeTriggerConfig, TriggerType} from './RoutineTypes';
import {formatEventTrigger, formatTimeTrigger} from './RoutineUtils';

interface RoutineCardProps {
  routine: Routine;
  onClick?: (routine: Routine) => void;
  // Runs the routine immediately. The run control is rendered only when this is provided.
  onRun?: (routine: Routine) => Promise<void>;
}

const useStyles = createStyles((theme) => ({
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.colors.gray[5],
    },
  },
  prompt: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  triggerIcon: {
    fontSize: 12,
    color: theme.colors.gray[6],
    flexShrink: 0,
  },
  // Fills the Paper so the clickable region covers the whole card; the run button sits alongside
  // it as a sibling (not nested) so we never put a button inside a button.
  clickableRegion: {
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'left',
  },
  // Keep a fixed round button (the flex row would otherwise squish it into an oval for long
  // prompts).
  runButton: {
    flexShrink: 0,
    backgroundColor: theme.colors.gray[2],
    '&:hover': {
      backgroundColor: theme.colors.gray[3],
    },
  },
  // Reserves the run button's footprint (matching the xs ActionIcon width) so the prompt/text
  // region is the same width whether or not the button is shown — event-based cards (no button)
  // line up with time-based cards.
  runButtonSlot: {
    flexShrink: 0,
    width: 18,
  },
}));

export default function RoutineCard({routine, onClick, onRun}: RoutineCardProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [isRunning, setIsRunning] = useState(false);
  const isTimeBased = routine.triggerType === TriggerType.TIME_BASED;
  const triggerText = isTimeBased
    ? formatTimeTrigger(routine.triggerConfig as TimeTriggerConfig)
    : formatEventTrigger(routine.triggerConfig as EventTriggerConfig);
  const displayName = routine.name ?? 'Untitled Agent';
  // Only time-based routines can be run manually: event-based triggers depend on data events that
  // haven't happened yet, so a manual run of them is ill-defined.
  const showRunButton = onRun != null && isTimeBased;

  const handleRun = async (): Promise<void> => {
    if (onRun == null) {
      return;
    }
    setIsRunning(true);
    try {
      await onRun(routine);
    } finally {
      setIsRunning(false);
    }
  };

  const cardBody = (
    <Stack spacing={4} style={{minWidth: 0}}>
      <Text size="sm" weight={600} truncate={true}>
        {displayName}
      </Text>
      <Text size="xs" color="dimmed" className={classes.prompt}>
        {routine.prompt}
      </Text>
      <Group spacing={4} noWrap={true}>
        <BenchlingIcon icon={isTimeBased ? 'clock' : 'sparkle'} className={classes.triggerIcon} />
        <Text size="xs" color="dimmed" style={{whiteSpace: 'nowrap'}}>
          {triggerText}
        </Text>
      </Group>
    </Stack>
  );

  const clickableRegion =
    onClick != null ? (
      <UnstyledButton
        aria-label={displayName}
        className={classes.clickableRegion}
        onClick={() => {
          onClick(routine);
        }}
      >
        {cardBody}
      </UnstyledButton>
    ) : (
      <Box className={classes.clickableRegion}>{cardBody}</Box>
    );

  return (
    <Paper withBorder={true} p="sm" className={cx(onClick != null && classes.clickable)}>
      <Group spacing="xs" noWrap={true} align="flex-start" position="apart">
        {clickableRegion}
        {showRunButton ? (
          <Tooltip label="Run now">
            <ActionIcon
              aria-label={`Run ${displayName} now`}
              className={classes.runButton}
              variant="light"
              color="gray"
              radius="xl"
              size="xs"
              loading={isRunning}
              onClick={handleRun}
            >
              <BenchlingIcon icon="play" />
            </ActionIcon>
          </Tooltip>
        ) : (
          // Placeholder keeps the text width identical to cards that do show the button.
          <Box className={classes.runButtonSlot} />
        )}
      </Group>
    </Paper>
  );
}
